import React, { useContext, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { classes, media, style } from 'typestyle'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'
import { runOperator } from '../../../core/operators'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { get } from '../../../share/find'
import { flat } from '../../../share/obj-equal'

/* Hooks ======================================================================================== */
import { useTheme } from '../../../components/theme'
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import AppLayout from '../../routes/app/layout'
import { Box } from '../../../components/box/box.component'
import { Line } from '../../../components/line'
import { Select } from '../../../components/select'
import { Snippet, SnippetBody } from '../../../modules/layouts/snippet'
import { Tabs, Tab } from '../../../components/tab'
import { Text } from '../../../components/text/text.component'
import { renderView } from '../../../modules/handlers/render-view'

/* Styles ======================================================================================= */
import { paddingLeftInner, paddingHorizontalInner } from '../../../styles/default/padding'
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { mt4 } from '../../../styles/styleset/margin/mt4'
import { p8 } from '../../../styles/styleset/padding/p8'
import { pb12 } from '../../../styles/styleset/padding/pb12'
import { pt12 } from '../../../styles/styleset/padding/pt12'

export default addSnippet('History', (props: any) => {
  const { row, $row } = props
  const { navigation } = useContext<any>(NavigationContext)
  const { rowId, groupId } = useParams()
  const [local] = useLocal()
  const [searchParams] = useSearchParams()
  const [type, $type] = useValue(searchParams.get('part') || 'Daily')
  const [trigger, $trigger] = useValue(null)

  const base = get('payload.base', navigation, 'object')
  const sections = get('payload.base.form.sections', navigation, 'array')
  const fields = get('payload.base.form.fields', navigation, 'array')

  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/history/${base.id}/${rowId}/${type}`
  const [data, $data] = useFetch({ data: [] }, { url: apiUrl })

  const [selected, $selected] = useValue(0)

  useEffect(() => {
    window.history.replaceState(
      '',
      '',
      `/a/groups/${groupId}/base/${base.id}/table/${rowId}?section=History`,
    )

    return () => {
      window.history.replaceState('', '', `/a/groups/${groupId}/base/${base.id}/table/${rowId}`)
    }
  }, [])

  useEffect(() => {
    if (data.mounted) {
      $data.get()
    }
  }, [data.mounted])

  useEffect(() => {
    if (trigger) {
      $data.get({
        url: `${process.env.REACT_APP_API_URL}/a/snippets/history/${base.id}/${rowId}/${type}`,
      })
    }
  }, [trigger, type])

  useEffect(() => {
    $row.set(selected === -1 ? row.payload : data.payload.data[selected])
  }, [selected, data.payload])

  const [current] = useTheme()
  const appLayoutContext = useContext(AppLayout.context)
  const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)
  const selectedRow = selected === -1 ? row.payload : data.payload.data[selected]

  return (
    <Box baseline="none" flex style={{ gap: 16 }}>
      <Box baseline="none" style={{ minWidth: 304 }}>
        <Box baseline="none" style={{ top: getOffsetTop() }} sticky>
          <Snippet>
            <Box className={classes(paddingLeftInner, pt12, pb12)}>
              <Text size="xs">Range</Text>
              <Select
                fullWidth
                size="lg"
                renderValue={value => {
                  return (
                    <Box flex alignCenterY baseline="none">
                      <Text size="sm" shrink>
                        {value}
                      </Text>
                    </Box>
                  )
                }}
                options={['Daily', 'Weekly', 'Monthly']}
                value={type}
                onChange={data => {
                  $type.set(data)
                  $trigger.set(Date.now())
                  $selected.set(0)
                }}
              />
            </Box>
            <SnippetBody>
              <Box flex row>
                {data.payload.data.map((row, key) => {
                  const { createdBy, updatedBy } = row as any

                  return (
                    <React.Fragment key={key}>
                      {key !== 0 && <Line className={classes(mt4, mb4)} />}
                      <Box
                        className={p8}
                        rounded
                        onClick={() => $selected.set(key)}
                        row
                        style={{
                          cursor: 'pointer',
                          background:
                            selected === key && current?.baselines?.primary?.color?.medium,
                        }}
                      >
                        <Text
                          size="sm"
                          semiBold
                          shrink
                          baseline={selected === key ? 'primary' : 'surface'}
                        >
                          {(row as any).historyLabel}
                        </Text>
                        <Text
                          baseline={selected === key ? 'primary' : 'surface'}
                          size="xs"
                          shrink
                          className={mt4}
                        >
                          Updated{' '}
                          {updatedBy ? updatedBy?.body?.fullName : createdBy?.body?.fullName}
                        </Text>
                      </Box>
                    </React.Fragment>
                  )
                })}
              </Box>
            </SnippetBody>
          </Snippet>
        </Box>
      </Box>
      <Box style={{ width: 'calc(100% - 324px)' }} baseline="none">
        <Snippet>
          <Box baseline="none" relative>
            {/* {sections && sections.length > 0 && (
              <HelpFlag
                identifier="aeki-help-row-tab-header"
                message={GLOBAL_CONTENTS.helpTextTab[local]}
                className={style({ right: 0, top: -20, zIndex: 3 })}
              />
            )} */}
            {(() => {
              const flatten: any = flat(selectedRow)

              const value: any = selectedRow
              fields &&
                fields.map((field: any) => {
                  switch (field.type) {
                    default:
                      if (field?.name?.indexOf('body.') === -1) field.name = `body.${field.name}`
                      break
                  }
                })
              if (sections && sections.length > 0) {
                return (
                  <>
                    <Tabs topOffset={getOffsetTop()}>
                      {sections &&
                        sections.map((item: any, key: number) => {
                          const { hidden } = runOperator(item, {
                            row: flatten,
                          })

                          const higlightTab = Object.keys(selectedRow?.diff || {}).some(
                            (key: any) => {
                              return key.includes(item.name)
                            },
                          )

                          return (
                            <Tab
                              label={item.label}
                              key={key}
                              hidden={hidden}
                              highlight={higlightTab}
                            >
                              <Box
                                className={classes(
                                  paddingLeftInner,
                                  paddingHorizontalInner,
                                  style({
                                    ...media({ type: 'print' }, { marginTop: 50 }),
                                    $nest: {
                                      '.aeki-box': {
                                        pageBreakInside: 'avoid',
                                      },
                                    },
                                  }),
                                )}
                                baseline="none"
                              >
                                {item.fields &&
                                  renderView({
                                    fields: item.fields,
                                    // hiddenFields: parseHiddenFields(
                                    //   hiddenFields[`body_${item.name}`],
                                    // ),
                                    highlightedFields: selectedRow?.diff,
                                    parentName: `body.${item.name}`,
                                    parentRowId: selectedRow?.parentRow?.id,
                                    flatten,
                                    value,
                                    isEditable: false,
                                    pathName: location.pathname,
                                  })}
                              </Box>
                            </Tab>
                          )
                        })}
                    </Tabs>
                  </>
                )
              } else if (fields && fields.length > 0) {
                return (
                  <Box
                    className={classes(paddingLeftInner, paddingHorizontalInner)}
                    baseline="none"
                    relative
                  >
                    {renderView({
                      fields,
                      // hiddenFields: parseHiddenFields(hiddenFields),
                      highlightedFields: selectedRow?.diff,
                      parentName: null,
                      parentRowId: selectedRow?.parentRow?.id,
                      flatten,
                      value,
                      local,
                      isEditable: false,
                      pathName: location.pathname,
                    })}
                  </Box>
                )
              }
            })()}
          </Box>
        </Snippet>
      </Box>
    </Box>
  )
})
